import React, { useState, useEffect, useRef } from "react";
import { api, track } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Shield } from "lucide-react";

export default function ContactForm({ defaultType = "devis", title, description }) {
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "", service: "", zone: "", type: defaultType });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [antispam, setAntispam] = useState(null); // public config fetched on mount
  const [captchaAnswer, setCaptchaAnswer] = useState("");
  const [honeypot, setHoneypot] = useState(""); // bot trap — must stay empty
  const startedAt = useRef(Date.now());

  useEffect(() => {
    // Fetch public anti-spam config (honeypot field name, captcha, etc.)
    let alive = true;
    api.get("/forms/antispam-config")
      .then(r => { if (alive) setAntispam(r.data || { enabled: false }); })
      .catch(() => { if (alive) setAntispam({ enabled: false }); });
    return () => { alive = false; };
  }, []);

  const refreshCaptcha = async () => {
    try {
      const { data } = await api.get("/forms/antispam-config");
      setAntispam(data);
      setCaptchaAnswer("");
    } catch { /* ignore */ }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Nom et téléphone requis"); return;
    }
    setLoading(true);
    try {
      const honeypotField = antispam?.honeypot_field || "website";
      const payload = {
        ...form,
        form_started_at: startedAt.current,
        [honeypotField]: honeypot, // bot trap; stays empty for humans
      };
      if (antispam?.math_captcha && antispam?.captcha?.token) {
        payload.captcha_answer = captchaAnswer;
        payload.meta = { ...(payload.meta || {}), captcha_token: antispam.captcha.token };
      }
      await api.post("/leads", payload);
      await track("devis_submit", { meta: { service: form.service, type: form.type } });
      setSent(true);
      toast.success("Votre demande a bien été envoyée — nous vous recontactons sous 24h.");
      setForm({ name: "", phone: "", email: "", message: "", service: "", zone: "", type: defaultType });
      setCaptchaAnswer("");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail) toast.error(detail);
      else toast.error("Une erreur est survenue. Réessayez ou appelez-nous.");
      // Refresh captcha on failure to avoid replay confusion
      if (antispam?.math_captcha) refreshCaptcha();
    } finally { setLoading(false); }
  };

  if (sent) {
    return (
      <div className="p-10 rounded-3xl bg-emerald-50 border border-emerald-200 text-center" data-testid="contact-form-success">
        <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto mb-4"/>
        <h3 className="font-display text-2xl font-bold text-emerald-900 mb-2">Demande reçue !</h3>
        <p className="text-emerald-700 mb-6">Nous vous contactons sous 24h ouvrées.</p>
        <Button onClick={() => setSent(false)} variant="outline" className="rounded-full">Envoyer une autre demande</Button>
      </div>
    );
  }

  const honeypotField = antispam?.honeypot_field || "website";

  return (
    <form onSubmit={submit} className="space-y-4 bg-white p-6 md:p-8 rounded-3xl shadow-soft border border-[hsl(var(--border))]" data-testid="contact-form">
      {title && <div>
        <h3 className="font-display text-2xl md:text-3xl font-bold text-[color:var(--brand-navy)]">{title}</h3>
        {description && <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2">{description}</p>}
      </div>}

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="name" className="text-xs font-semibold uppercase tracking-wider">Nom complet *</Label>
          <Input id="name" data-testid="input-name" value={form.name} onChange={e=>setForm({...form, name:e.target.value})} required className="mt-1.5 h-11 rounded-xl"/>
        </div>
        <div>
          <Label htmlFor="phone" className="text-xs font-semibold uppercase tracking-wider">Téléphone *</Label>
          <Input id="phone" data-testid="input-phone" type="tel" value={form.phone} onChange={e=>setForm({...form, phone:e.target.value})} required className="mt-1.5 h-11 rounded-xl"/>
        </div>
      </div>

      <div>
        <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider">Email</Label>
        <Input id="email" data-testid="input-email" type="email" value={form.email} onChange={e=>setForm({...form, email:e.target.value})} className="mt-1.5 h-11 rounded-xl"/>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider">Type de demande</Label>
          <Select value={form.type} onValueChange={(v)=>setForm({...form, type:v})}>
            <SelectTrigger data-testid="select-type" className="mt-1.5 h-11 rounded-xl"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="devis">Devis gratuit</SelectItem>
              <SelectItem value="rappel">Demande de rappel</SelectItem>
              <SelectItem value="contact">Question / info</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="service" className="text-xs font-semibold uppercase tracking-wider">Besoin</Label>
          <Select value={form.service} onValueChange={(v)=>setForm({...form, service:v})}>
            <SelectTrigger data-testid="select-service" className="mt-1.5 h-11 rounded-xl"><SelectValue placeholder="Choisir..."/></SelectTrigger>
            <SelectContent>
              <SelectItem value="Installation climatisation">Installation climatisation</SelectItem>
              <SelectItem value="Plomberie">Plomberie</SelectItem>
              <SelectItem value="Dépannage">Dépannage urgent</SelectItem>
              <SelectItem value="Entretien">Entretien / contrat</SelectItem>
              <SelectItem value="Autre">Autre</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label htmlFor="zone" className="text-xs font-semibold uppercase tracking-wider">Ville / Code postal</Label>
        <Input id="zone" data-testid="input-zone" value={form.zone} onChange={e=>setForm({...form, zone:e.target.value})} placeholder="ex: Salon-de-Provence 13300" className="mt-1.5 h-11 rounded-xl"/>
      </div>

      <div>
        <Label htmlFor="message" className="text-xs font-semibold uppercase tracking-wider">Votre message</Label>
        <Textarea id="message" data-testid="input-message" value={form.message} onChange={e=>setForm({...form, message:e.target.value})} rows={4} className="mt-1.5 rounded-xl" placeholder="Décrivez brièvement votre projet ou votre problème..."/>
      </div>

      {/* --- Captcha mathématique (si activé dans les paramètres) --- */}
      {antispam?.math_captcha && antispam?.captcha && (
        <div className="flex items-end gap-3 p-4 rounded-2xl border border-[color:var(--brand-sky)]/20 bg-[color:var(--brand-sky)]/5" data-testid="antispam-captcha">
          <div className="flex-1">
            <Label htmlFor="captcha" className="text-xs font-semibold uppercase tracking-wider flex items-center gap-1.5">
              <Shield className="w-3.5 h-3.5 text-[color:var(--brand-sky)]" />
              Vérification anti-spam
            </Label>
            <div className="mt-1.5 flex items-center gap-2">
              <span className="font-mono text-lg font-bold text-[color:var(--brand-navy)]" data-testid="captcha-question">{antispam.captcha.question}</span>
              <Input
                id="captcha"
                inputMode="numeric"
                required
                value={captchaAnswer}
                onChange={e => setCaptchaAnswer(e.target.value)}
                placeholder="Réponse"
                className="h-11 w-28 rounded-xl font-mono text-center"
                data-testid="input-captcha"
              />
              <button
                type="button"
                onClick={refreshCaptcha}
                className="text-xs text-[color:var(--brand-sky)] hover:underline"
                title="Nouvelle question"
              >
                ↻
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- Honeypot — invisible pour humains, rempli par bots --- */}
      <div
        aria-hidden="true"
        style={{ position: "absolute", left: "-9999px", top: "-9999px", height: 0, width: 0, overflow: "hidden" }}
      >
        <label htmlFor={honeypotField}>Laissez ce champ vide</label>
        <input
          type="text"
          id={honeypotField}
          name={honeypotField}
          tabIndex={-1}
          autoComplete="off"
          value={honeypot}
          onChange={e => setHoneypot(e.target.value)}
        />
      </div>

      <Button type="submit" disabled={loading} className="w-full h-12 rounded-full bg-[color:var(--brand-sky)] hover:bg-[color:var(--brand-sky)]/90 text-white font-bold text-base" data-testid="submit-contact-form">
        {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin"/> Envoi...</> : "Envoyer ma demande"}
      </Button>
      <p className="text-[10px] text-[hsl(var(--muted-foreground))] text-center uppercase tracking-wider flex items-center justify-center gap-1.5">
        <Shield className="w-3 h-3" /> Formulaire sécurisé · Réponse sous 24h · 100% gratuit
      </p>
    </form>
  );
}
